import { RHI_V2_MAX_VISIBLE_FEATURES } from '@/constants/healthInsuranceConfig';
import {
  IRhiV2Cover,
  IRhiV2PaymentSummary,
  IRhiV2Plan,
  IRhiV2PlanFeatures,
  IRhiV2Selection,
  IRhiV2UnavailablePlan,
  TRhiV2PlanFeatureFooterKind,
} from '@/types/rhiV2';
import { QuoteType } from '@/types';

const formatPremium = (payableCent: number, withDecimal: boolean) =>
  Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: withDecimal ? 2 : 0,
    maximumFractionDigits: withDecimal ? 2 : 0,
  }).format(payableCent / 100);

/** An add-on's price as a change to the premium: "+ ₹1,780", or "−₹520" for a discount. */
export const formatAddonPrice = (cent: number) =>
  cent < 0 ? `−${formatRupee(-cent)}` : `+ ${formatRupee(cent)}`;

export const formatRupee = (cent: number) =>
  Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cent / 100);

export const formatSumInsured = (rupees: number) =>
  rupees >= 10000000
    ? `₹${Number((rupees / 10000000).toFixed(2))} Crore`
    : `₹${Number((rupees / 100000).toFixed(2))} Lakh`;

export const formatTenure = (years: number) =>
  `${years} ${years > 1 ? 'years' : 'year'}`;

export const formatCover = (cover: {
  sumInsuredCent: number | null;
  sumInsuredUnlimited: boolean;
}) => {
  if (cover.sumInsuredUnlimited) return 'Unlimited';
  if (cover.sumInsuredCent === null) return '—';
  return formatSumInsured(cover.sumInsuredCent / 100);
};

export const rhiV2PlanToQuote = (
  plan: IRhiV2Plan,
  plumQuoteId: string,
): QuoteType => ({
  quoteId: plumQuoteId,
  planId: String(plan.planId),
  title: plan.product.name,
  imageUrl: plan.insurer.logoUrl ?? '',
  IsPlanRecommendedField: false,
  premium: {
    centAmount: plan.premium.payableCent,
    currency: plan.premium.currency,
    currencyNativeString: formatPremium(plan.premium.payableCent, true),
    currencyNativeStringWithoutDecimal: formatPremium(
      plan.premium.payableCent,
      false,
    ),
  },
  features: hasPlanFeatures(plan.features)
    ? plan.features.points.map((point) => ({ title: point, imageUrl: '' }))
    : [],
  availableAtExtracost: [],
  additionalRiders: [],
});

// HDFC_OPTIMA_SECURE -> HDFC Optima Secure when the quote has no name for it,
// spelling words the way the insurer's own name does. No product code means the
// whole insurer is missing.
export const unavailablePlanName = (
  item: IRhiV2UnavailablePlan,
  plans: IRhiV2Plan[],
) => {
  const { productCode } = item;
  if (!productCode) return 'All plans';
  const knownName = plans.find((plan) => plan.product.code === productCode)
    ?.product.name;
  if (knownName) return knownName;
  const insurerWords = item.insurer.name.split(' ');
  return productCode
    .split('_')
    .map(
      (word) =>
        insurerWords.find((known) => known.toUpperCase() === word) ??
        word.charAt(0) + word.slice(1).toLowerCase(),
    )
    .join(' ');
};

/** The add-on master's name, shown as the server sends it. */
export const coverLabel = (cover: Pick<IRhiV2Cover, 'name'>) => cover.name;

/** A per-member add-on's chip: "Self · 34 yrs", with the age from the plan page's members. */
export const coverMemberLabel = (
  relation: string,
  members: Array<{ relation: string; age: number }>,
) => {
  const member = members.find(
    (item) => item.relation.toLowerCase() === relation.toLowerCase(),
  );
  const name = relation.charAt(0).toUpperCase() + relation.slice(1);
  return member ? `${name} · ${member.age} yrs` : name;
};

export const coverScopeNote = (cover: IRhiV2Cover) => {
  if (cover.coverScope !== 'member' || cover.eligibleMembers.length === 0) {
    return undefined;
  }

  const members = cover.eligibleMembers
    .map((relation) => relation.charAt(0).toUpperCase() + relation.slice(1))
    .join(', ');

  return `Applies to ${members}`;
};

/** The other add-ons sharing this one's group; only one add-on of a group can be added. */
export const addonGroupPartners = (covers: IRhiV2Cover[], cover: IRhiV2Cover) =>
  cover.groupCode
    ? covers.filter(
        (item) => item.groupCode === cover.groupCode && item.coverCode !== cover.coverCode,
      )
    : [];

export type TAddonTile =
  | { kind: 'single'; cover: IRhiV2Cover }
  | { kind: 'group'; groupCode: string; covers: IRhiV2Cover[] };

/**
 * The add-ons card in order: each lone add-on is its own tile, then each set of add-ons
 * sharing a group as one "pick 1 of N" tile, groups in the order their first add-on came.
 */
export const addonTiles = (covers: IRhiV2Cover[]): TAddonTile[] => {
  const tiles = covers.flatMap((cover): TAddonTile[] => {
    const partners = addonGroupPartners(covers, cover);
    if (!cover.groupCode || partners.length === 0) return [{ kind: 'single', cover }];
    const isFirstOfGroup = covers.find((item) => item.groupCode === cover.groupCode) === cover;
    return isFirstOfGroup
      ? [{ kind: 'group', groupCode: cover.groupCode, covers: [cover, ...partners] }]
      : [];
  });
  return [
    ...tiles.filter((tile) => tile.kind === 'single'),
    ...tiles.filter((tile) => tile.kind === 'group'),
  ];
};

/**
 * Plum's copy for a plan, only when it is an object (client guide 2026-10-30 §4): an older
 * backend's list, null or a missing key shows no features, and never breaks the card.
 */
export const hasPlanFeatures = (features: unknown): features is IRhiV2PlanFeatures =>
  typeof features === 'object' && features !== null && !Array.isArray(features);

/** Footer headings are the client's to write, by kind (client guide 2026-10-30 §3). */
export const RHI_V2_PLAN_FEATURE_HEADINGS: Record<TRhiV2PlanFeatureFooterKind, string> = {
  COVERS: 'Covers',
  WONT_COVER: "Won't cover",
  GOOD_TO_KNOW: 'Good to know',
};

/** The footer sections this app can head, in the order sent; new kinds are skipped. */
export const planFeatureFooter = ({ footer }: IRhiV2PlanFeatures) =>
  footer.filter(({ kind }) => kind in RHI_V2_PLAN_FEATURE_HEADINGS);

/** The card shows the tagline and the first points; the rest is on the plan page. */
export const hasMorePlanFeatures = (features: IRhiV2PlanFeatures) =>
  features.points.length > RHI_V2_MAX_VISIBLE_FEATURES ||
  planFeatureFooter(features).length > 0;

/**
 * An added add-on at ₹0: the insurer charges it inside the base premium, so it isn't free
 * (guide 2026-10-16 §3).
 */
export const RHI_V2_ADDON_INCLUDED_LABEL = 'Included in premium';

/** An add-on choice as shown: the option with its unit ("20 Percent"), or its own cover. */
export const formatAddonChoice = ({
  option,
  optionLabel,
  sumInsuredCent,
}: {
  option?: string | null;
  optionLabel?: string | null;
  sumInsuredCent?: number | null;
}) =>
  [
    option ? [option, optionLabel].filter(Boolean).join(' ') : null,
    typeof sumInsuredCent === 'number' ? formatRupee(sumInsuredCent) : null,
  ]
    .filter(Boolean)
    .join(', ');

export const titleCase = (value: string) =>
  value
    .split(/[_-]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

export interface IRhiV2PremiumLine {
  key: string;
  label: string;
  amountCent: number | null;
  /** Plan lines (base, built-in covers, discount) list before the add-ons the customer chose. */
  group: 'plan' | 'addon';
}

/** What the premium bar shows on every step: the total and how it adds up. */
export interface IRhiV2Premium {
  totalCent: number | null;
  tenureYears: number;
  /** Empty when the server sends no split; then only the total shows. */
  lines: IRhiV2PremiumLine[];
}

const memberNames = (members: { relation: string }[]) =>
  members.map((member) => titleCase(member.relation)).join(', ');

/** "₹5 L cover · 1 year · Self, Spouse", the line under the plan name everywhere. */
export const planSummaryLine = (
  cover: Parameters<typeof formatCover>[0] & { tenureYears: number },
  members: { relation: string }[],
) => `${formatCover(cover)} cover · ${formatTenure(cover.tenureYears)} · ${memberNames(members)}`;

export const premiumFromSummary = (summary: IRhiV2PaymentSummary): IRhiV2Premium => {
  const { payableCent, basePremiumCent } = summary.price;

  return {
    totalCent: payableCent,
    tenureYears: summary.cover.tenureYears,
    lines: [
      ...(typeof basePremiumCent === 'number'
        ? [{ key: 'base', label: 'Base premium', amountCent: basePremiumCent, group: 'plan' as const }]
        : []),
      ...summary.addons.map((addon) => ({
        key: addon.coverCode,
        // "Borderless — 20 Percent", "Personal Accident — ₹10,00,000"
        label: [coverLabel(addon), formatAddonChoice(addon)]
          .filter(Boolean)
          .join(' — '),
        amountCent: addon.premiumCent,
        group: addon.isMandatory ? ('plan' as const) : ('addon' as const),
      })),
    ],
  };
};

export const premiumTermLabel = (tenureYears: number) =>
  tenureYears > 1 ? `for ${tenureYears} years` : '/ year';

export const premiumTermNote = (tenureYears: number) =>
  `For the full ${tenureYears}-year policy term.`;

/**
 * The plan-details premium, straight from the server: base + add-ons − discount = payable.
 * `addonsCent` covers the always-included covers as well as the picked add-ons, so both are
 * listed. Each gets its own line when the server priced every one of them; otherwise they show
 * as the one `addonsCent` line. Nothing is summed here.
 */
export const premiumFromSelection = (selection: IRhiV2Selection): IRhiV2Premium => {
  const { baseCent, addonsCent = null, discountCent, payableCent } = selection.premium;
  const billed = [
    ...selection.mandatoryCovers,
    ...selection.optionalCovers.filter((cover) => cover.selected),
  ];
  const hasSplit = [baseCent, addonsCent, discountCent].some((cent) => cent !== null);

  const addonLines: IRhiV2PremiumLine[] = billed.every((cover) => cover.premiumCent !== null)
    ? billed.map((cover) => ({
        key: cover.coverCode,
        label: coverLabel(cover),
        amountCent: cover.premiumCent,
        group: 'addon',
      }))
    : [{ key: 'addons', label: 'Add-ons', amountCent: addonsCent, group: 'addon' }];

  return {
    totalCent: payableCent,
    tenureYears: selection.tenureYears,
    lines: hasSplit
      ? [
          ...(baseCent !== null
            ? [{ key: 'base', label: 'Base premium', amountCent: baseCent, group: 'plan' as const }]
            : []),
          ...(discountCent
            ? [{ key: 'discount', label: 'Discount', amountCent: -discountCent, group: 'plan' as const }]
            : []),
          ...(billed.length ? addonLines : []),
        ]
      : [],
  };
};
